const SHEET_ID = import.meta.env.VITE_SPREADSHEET_ID;
const TOKEN_URL = import.meta.env.VITE_GRETA_TOKEN_URL;
const CHAT_ID = import.meta.env.VITE_CHAT_ID;
const API_BASE = `https://sheets.googleapis.com/v4/spreadsheets/${SHEET_ID}`;

let cachedToken = { token: null, exp: 0 };
let sheetMeta = null;

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

async function withRetry(request) {
  let attempt = 0;

  while (attempt < 4) {
    const response = await request();

    if (response.status !== 429 || attempt === 3) {
      return response;
    }

    await wait(400 * 2 ** attempt);
    attempt += 1;
  }

  return request();
}

export async function getAccessToken() {
  if (cachedToken.token && Date.now() < cachedToken.exp) {
    return cachedToken.token;
  }

  const response = await fetch(TOKEN_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ chatId: CHAT_ID }),
  });

  const json = await response.json();

  if (!response.ok || !json.success) {
    throw new Error(json.error || 'Unable to authorize Google Sheets access.');
  }

  const expiresIn = Number(json.expires_in || 3600);
  cachedToken = {
    token: json.access_token,
    exp: Date.now() + Math.max(expiresIn - 60, 30) * 1000,
  };

  return cachedToken.token;
}

export async function sheetsRequest(path, init = {}) {
  const token = await getAccessToken();
  const response = await withRetry(() => fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      ...(init.headers || {}),
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  }));

  if (!response.ok) {
    throw new Error(`Google Sheets request failed (${response.status}).`);
  }

  return response.status === 204 ? {} : response.json();
}

export async function getRows(range) {
  const result = await sheetsRequest(`/values/${encodeURIComponent(range)}`);
  return result.values || [];
}

export async function appendRow(range, values) {
  return sheetsRequest(
    `/values/${encodeURIComponent(range)}:append?valueInputOption=USER_ENTERED&insertDataOption=INSERT_ROWS`,
    {
      method: 'POST',
      body: JSON.stringify({ values: [values] }),
    },
  );
}

export async function updateRow(range, values) {
  return sheetsRequest(
    `/values/${encodeURIComponent(range)}?valueInputOption=USER_ENTERED`,
    {
      method: 'PUT',
      body: JSON.stringify({ values: [values] }),
    },
  );
}

export async function deleteRow(title, id) {
  const sheetId = await getSheetIdByTitle(title);
  const ids = await getRows(`${title}!A:A`);
  const rowIndex = ids.findIndex((row, index) => index > 0 && row?.[0] === id);

  if (sheetId === undefined) {
    throw new Error(`Sheet tab "${title}" was not found.`);
  }

  if (rowIndex < 0) {
    throw new Error('The requested RSVP could not be found.');
  }

  return sheetsRequest('/:batchUpdate', {
    method: 'POST',
    body: JSON.stringify({
      requests: [{
        deleteDimension: {
          range: {
            sheetId,
            dimension: 'ROWS',
            startIndex: rowIndex,
            endIndex: rowIndex + 1,
          },
        },
      }],
    }),
  });
}

export async function getSheetMeta(force = false) {
  if (!force && sheetMeta) {
    return sheetMeta;
  }

  const result = await sheetsRequest('/?fields=sheets.properties');
  sheetMeta = Object.fromEntries(
    (result.sheets || []).map((sheet) => [
      sheet.properties.title,
      sheet.properties.sheetId,
    ]),
  );

  return sheetMeta;
}

export async function getSheetIdByTitle(title) {
  let meta = await getSheetMeta();

  if (meta[title] === undefined) {
    meta = await getSheetMeta(true);
  }

  return meta[title];
}

function columnLetter(number) {
  let result = '';

  while (number > 0) {
    const remainder = (number - 1) % 26;
    result = String.fromCharCode(65 + remainder) + result;
    number = Math.floor((number - 1) / 26);
  }

  return result;
}

export async function ensureTab(title, headers) {
  let meta = await getSheetMeta();

  if (meta[title] === undefined) {
    await sheetsRequest('/:batchUpdate', {
      method: 'POST',
      body: JSON.stringify({
        requests: [{
          addSheet: { properties: { title } },
        }],
      }),
    });

    sheetMeta = null;
    meta = await getSheetMeta(true);
  }

  const endColumn = columnLetter(headers.length);
  const currentHeader = await getRows(`${title}!A1:${endColumn}1`);

  if (!currentHeader[0]?.length) {
    await updateRow(`${title}!A1:${endColumn}1`, headers);
  }
}